import React, { useState, useCallback, useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";
import Table from "../../ui-components/table/table";
import Button, { ButtonThemes } from "../../ui-components/button/button";
import "./loadboardDispatchList.scss";
import ContentContainer from "templates/content-container/contentContainer";
import { PAGE_IDS } from "app/data/common/pageIds";
import LabeledTextInput from "ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import { LabelModes } from "ui-components/molecules/labeled-inputs/labeledInput";
import XGSErrorMessage from "ui-components/error-message/errorMessage";
import XGSFormSelect from "ui-components/form/select/xgsFormSelect";
import { Formik } from 'formik';
// import Switch from "react-switch";
import ContentContainerToolbar from "ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import AssignDoorModal from "features/dispatch/assign-door";
import CloseDoorModal from "./CloseDoorModal/closeDoorModal";
// import MainfestFreightDetails from "./MainfestFreightDetails/mainfestFreightDetails";
import { useLoadboardManifestTrips } from "../../hooks/useLoadboard";
import { LoadboardListManifestRequestParams, LoadboardListManifestTrip, statusOptions } from "../../app/data/load-board/models";
import { useDestinationTerminals } from "hooks/usePickupSummary";
import LabeledDateRangeInput from "ui-components/molecules/labeled-inputs/labeled-date-range-input/labeledDateRangeInput";
import ProgressBar from "../../ui-components/progress-bar/progressBar";
import { showPrettyTime } from "features/dock-loading/common";
import { useCarrierDropdown } from "hooks/useCarrier";
import { QUERY_KEYS } from "app/data/common/queryKeys";
import LoadBoardService from "app/data/load-board/loadBoardService";
import { useQuery } from "@tanstack/react-query";
import Tag from "ui-components/molecules/tag/tag";
import { TagColor } from "app/data/common/tagColor";
import { userSelector } from "../../slices/user/userSlice";
import { useSelector } from "react-redux";
import { AssignDoorFormValues } from "features/dispatch/assign-door/models";
import { ReactComponent as AssignDoor } from "../../images/assign_door_blue.svg";
import { ReactComponent as CloseDoor } from "../../images/close_door_blue.svg";
import { ReactComponent as Dispatch } from "../../images/dispatch _door_blue.svg";
import DispatchLoadModal from "./DispatchLoadModal/dispatchLoadModal";

const LoadboardDispatchList: React.FC<{}> = () => {

  const loadBoardService = LoadBoardService.getInstance();

  const [searchInput, setSearchInput] = useState("");
  const userState = useSelector(userSelector);

  const terminalNumber = useMemo(() => {
    if (!userState.activeTerminal?.id) return 0;
    return userState.activeTerminal.id;
  }, [userState.activeTerminal?.id]);

  const [filterType, setFilterType] = useState<number>();
  const [filterStatus, setFilterStatus] = useState("");
  const [filterDoor, setFilterDoor] = useState("");
  const [filterDest, setFilterDest] = useState<number>();
  // const [showConfirmedProbills, setShowConfirmedProbills] = useState<boolean>(true);
  const [showAssignDoorModal, setShowAssignDoorModal] = useState<boolean>(false);
  // const [showMainfestFreightDetailsModal, setShowMainfestFreightDetailsModal] = useState<boolean>(false);

  const formatYYYYMMDD = (date: Date) => {
    return date.toISOString().split("T")[0];
  };

  const getInitialDateRange = () => {
    const today = new Date();

    const startDate = new Date(today);
    startDate.setDate(today.getDate() - 3);

    const endDate = new Date(today);
    endDate.setDate(today.getDate() + 3);

    return {
      start: formatYYYYMMDD(startDate),
      end: formatYYYYMMDD(endDate),
    };
  };

  const INITIAL_DATE_RANGE = getInitialDateRange();

  const [tempDateRange, setTempDateRange] = useState(INITIAL_DATE_RANGE);

  const [appliedFilters, setAppliedFilters] =
    useState<LoadboardListManifestRequestParams>({
      manifestOriginTerminalNumber: terminalNumber,
      startCutDate: INITIAL_DATE_RANGE.start,
      endCutDate: INITIAL_DATE_RANGE.end,
    });

  const [assignDoorInitialValues, setAssignDoorInitialValues] = useState<Partial<AssignDoorFormValues> | undefined>(undefined);
  const [editingManifestNumber, setEditingManifestNumber] = useState<number | undefined>(undefined);
  const [showCloseDoorModal, setShowCloseDoorModal] = useState(false);
  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [dispatchData, setDispatchData] = useState<any>(null);
  const [closedDoorData, setClosedDoorData] = useState<any>(null);

  const { data, isLoading: isManifestLoading, error: manifestError, refetch } = useLoadboardManifestTrips(
    React.useMemo(() => ({ ...appliedFilters }), [appliedFilters])
  );

  const allTrips = React.useMemo(() => {
    return data?.flatMap((page) => page) || [];
  }, [data]);

  const manifestData = React.useMemo(() => {
    const map = new Map<number, LoadboardListManifestTrip>();

    allTrips.forEach((trip) => {
      if (!map.has(trip.manifestNumber)) {
        map.set(trip.manifestNumber, trip);
      }
    });

    return Array.from(map.values());
  }, [allTrips]);



  const { data: doors, isLoading: isDoorsLoading } = useQuery({
    queryKey: [QUERY_KEYS.LOAD_BOARD_DOORS, terminalNumber],
    queryFn: async () => {
      if (!terminalNumber) return [];
      const response = await loadBoardService.getDoors(terminalNumber);
      return response.data || [];
    },
  });

  const doorOptions = useMemo(() => {
    return doors
      ?.filter(d => d.doorNumber)
      .map(d => ({
        label: d.doorNumber!,
        value: d.doorNumber!
      })) || [];
  }, [doors]);

  const {
    data: carrierDropdownData = [],
    isLoading: isCarrierLoading,
  } = useCarrierDropdown();

  const carrierOptions = useMemo(() => {
    return carrierDropdownData
      .filter(c => c?.carrierNumber && c?.carrierName)
      .map(c => ({
        value: c.carrierNumber,
        label: (
          <span>
            {c.carrierName}{" "}
            <span className="text-light">{c.carrierNumber}</span>
          </span>
        ),
        sortLabel: `${c.carrierName} ${c.carrierNumber}`,
      }))
      .sort((a, b) => a.sortLabel.localeCompare(b.sortLabel));
  }, [carrierDropdownData]);

  const { data: destinationterminalOptions = [], isLoading: destinationterminalsLoading } = useDestinationTerminals("");

  const getOption = (options: any[], value?: number | string) =>
    options.find((o) => o.value === value) ?? null;

  const onSearchStart = useCallback(() => {
    const newFilters: LoadboardListManifestRequestParams = {
      manifestOriginTerminalNumber: terminalNumber || undefined,
      manifestNumberSearch: searchInput.trim() || undefined,
      startCutDate: tempDateRange.start.toApiDateFormat() || undefined,
      endCutDate: tempDateRange.end.toApiDateFormat() || undefined,
      doorManifestStatus: filterStatus || undefined,
      carrierNumber: filterType || undefined,
      doorNumber: filterDoor || undefined,
      manifestDestinationTerminalNumber: filterDest || undefined,
    };

    setAppliedFilters(newFilters);
  }, [terminalNumber, searchInput, tempDateRange, filterStatus, filterType, filterDoor, filterDest]);

  const onSearchClear = (resetForm: any) => () => {
    setSearchInput("");
    setTempDateRange(INITIAL_DATE_RANGE);
    setFilterStatus("");
    setFilterType(undefined);
    setFilterDoor("");
    setFilterDest(undefined);
    resetForm({
      values: {
        doorManifestStatus: "",
        carrierNumber: "",
        doorNumber: "",
        manifestDestinationTerminalNumber: "",
      },
    });
    setAppliedFilters({
      manifestOriginTerminalNumber: terminalNumber,
      startCutDate: INITIAL_DATE_RANGE.start,
      endCutDate: INITIAL_DATE_RANGE.end,
    });
  };

  const getTripsByManifest = React.useCallback(
    (manifestNumber: number | string) => {
      return allTrips
        .filter(t => t.manifestNumber === manifestNumber)
        .sort((a, b) => Number(a.tripNumber) - Number(b.tripNumber));
    },
    [allTrips]
  );

  // const onShowAvailableFreightChanged = (checked: boolean) => {
  //   setShowConfirmedProbills(checked);
  // };

  const columns: ColumnDef<LoadboardListManifestTrip>[] = [
    {
      accessorKey: "manifestNumber",
      header: "Manifest",
      size: 80,
      cell: ({ row, getValue }) => {
        const canExpand = row.getCanExpand();

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              whiteSpace: "nowrap",
            }}
          >
            {canExpand && (
              <button
                onClick={row.getToggleExpandedHandler()}
                style={{
                  cursor: "pointer",
                  background: "none",
                  border: "none",
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <svg
                  width="12"
                  height="8"
                  viewBox="0 0 12 8"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  style={{
                    transform: row.getIsExpanded()
                      ? "rotate(0deg)"
                      : "rotate(-90deg)",
                    transition: "transform 0.2s ease-in-out",
                  }}
                >
                  <path
                    d="M12 1.20856L10.7914 -1.28746e-05L6 4.79142L1.20857 -1.28746e-05L0 1.20856L6 7.20856L12 1.20856Z"
                    fill="#828282"
                  />
                </svg>
              </button>
            )}
            {/* <span
              onClick={() => setShowMainfestFreightDetailsModal(true)}
              style={{ color: '#2F80ED', cursor: 'pointer', textDecoration: 'underline' }}
            > */}
            {getValue() as string}
            {/* </span> */}
          </div>
        );
      },
    },
    {
      accessorKey: "doorNumber",
      header: "Door",
      cell: ({ getValue }) => {
        return (
          <>
            {getValue() && <span>D-{getValue()}</span>}
          </>
        );
      },
      size: 50
    },
    {
      accessorKey: "doorPriority",
      header: "!",
      size: 10,
    },
    {
      header: "Trips",
      size: 20,
      cell: ({ row }) => {
        const manifestNumber = row.original.manifestNumber;
        const trips = allTrips.filter(t => t.manifestNumber === manifestNumber);
        if (!trips.length) return "-";

        const highestTrip = Math.max(...trips.map(t => Number(t.tripNumber)));
        return <span>{highestTrip}</span>;
      },
    },

    {
      id: "parentOrigin",
      header: "Origin",
      enableSorting: true,
      cell: ({ row }) => {
        const [trip1] = getTripsByManifest(row.original?.manifestNumber);

        if (!trip1) return "-";

        return (
          <span>
            {trip1.originTerminalAirportCode} - {trip1.originTerminalName}{" "}
            <span className="text-light">{trip1.originTerminalNumber}</span>
          </span>
        );
      },
      size: 130,
    },
    {
      id: "parentStop",
      header: "Stop",
      cell: ({ row }) => {
        const trips = getTripsByManifest(row.original.manifestNumber);
        const trip2 = trips[1];

        if (!trip2) return "-";

        return (
          <span>
            {trip2.originTerminalAirportCode} - {trip2.originTerminalName}{" "}
            <span className="text-light">{trip2.originTerminalNumber}</span>
          </span>
        );
      },
      size: 130,
    },
    {
      id: "parentDestination",
      header: "Destination",
      cell: ({ row }) => {
        const trips = getTripsByManifest(row.original.manifestNumber);
        const trip2 = trips[1] ?? trips[0]; // fallback safety

        if (!trip2) return "-";

        return (
          <span>
            {trip2.destinationTerminalAirportCode} - {trip2.destinationTerminalName}{" "}
            <span className="text-light">{trip2.destinationTerminalNumber}</span>
          </span>
        );
      },
      size: 130,
    },
    {
      accessorKey: "loadedWeight",
      header: "Load %",
      size: 50,
      cell: ({ getValue }) => {
        const loadedWeight = Number(getValue()) || 0;
        const MAX_CAPACITY = 42000;

        const loadPercentage = Math.min(
          Math.round((loadedWeight / MAX_CAPACITY) * 100),
          100
        );

        return (
          <ProgressBar
            percentage={loadPercentage}
            additionalInfo={showPrettyTime(`${loadPercentage}%`)}
            multicolor
          />
        );
      },
    },
    {
      accessorKey: "carrierNumber",
      header: "Carrier",
      size: 120,
      cell: ({ row }) => (
        <span>
          {row?.original?.carrierName} <span className="text-light">{row?.original?.carrierNumber}</span>
        </span>
      ),
      // cell: ({ row, column }) => {
      //   const isEditing =
      //     editingCell?.rowId === row.id &&
      //     editingCell?.columnId === column.id;

      //   if (isEditing) {
      //     return (
      //       <LabeledSelectInput
      //         className="xgs-loadboard-dispatch__carrier-edit-dropdown"
      //         options={carrierOptions}
      //         value={getOption(carrierOptions, Number(tempValue))}
      //       // onValueChange={(opt) => {
      //       //   const value = opt?.value ? String(opt.value) : "";
      //       //   setTempValue(value);
      //       //   saveEdit(row, column.id); // optional: auto-save on select
      //       // }}
      //       />
      //     );
      //   }

      //   return (
      //     <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
      //       <span>
      //         {row.original.carrierName}{" "}
      //         <span className="text-light">{row.original.carrierNumber}</span>
      //       </span>

      //       <EditIcon
      //         width={12}
      //         height={12}
      //         style={{ cursor: "pointer", flexShrink: 0 }}
      //         onClick={(e) => {
      //           e.stopPropagation();
      //           startEdit(row, column.id, row.original.carrierNumber);
      //         }}
      //       />
      //     </div>
      //   );
      // },
    },
    {
      id: "laneStatus",
      accessorKey: "doorManifestStatus",
      header: "Lane Status",
      size: 80,
      cell: ({ getValue }) => {
        const colorMap = {
          DEPARTED: TagColor.GREEN,
          LOADING: TagColor.BLUE,
          CLOSED: TagColor.ORANGE,
          UNASSIGNED: TagColor.GREY,
        };

        const value = getValue();

        return (
          <Tag mods={{ color: colorMap[value] ?? TagColor.GREY }}>
            {value}
          </Tag>
        );
      }
    },
    {
      id: "nextStep",
      accessorKey: "doorManifestStatus",
      header: "Next Step",
      size: 80,
      cell: ({ row }) => {
        const status = row.original?.doorManifestStatus;
        switch (status) {
          case "UNASSIGNED":
            return (
              <span
                onClick={() => {
                  setEditingManifestNumber(Number(row.original.manifestNumber));
                  setShowAssignDoorModal(true);
                }}
                style={{
                  color: "#2F80ED",
                  cursor: "pointer",
                  fontWeight: 500,
                }}
              >
                <AssignDoor /> Assign Door
              </span>
            );

          case "LOADING":
            return (
              <span
                onClick={() => {
                  setClosedDoorData(row.original);
                  setShowCloseDoorModal(true);
                }}
                style={{
                  color: "#2F80ED",
                  cursor: "pointer",
                  fontWeight: 500,
                }}>
                <CloseDoor /> Close Door
              </span>
            );

          case "CLOSED":
            return (
              <span
                onClick={() => {
                  setDispatchData(row.original);
                  setShowDispatchModal(true);
                }}
                style={{
                  color: "#2F80ED",
                  cursor: "pointer",
                  fontWeight: 500,
                }}
              >
                <Dispatch /> Dispatch
              </span>
            );

          case "DEPARTED":
            return <span>-</span>;

          default:
            return <span>-</span>;
        }
      },
    },
    {
      accessorKey: "plannedCloseTime",
      header: "Plan Close Time",
      cell: ({ row }) => (
        <span>
          {row?.original?.plannedCloseDate?.toUiDateFormat()} <span className="text-light">{row?.original?.plannedCloseTime?.toUiTimeFormat()}</span>
        </span>
      ),
      size: 100,
    },
    {
      accessorKey: "dispatchTime",
      header: "Dispatch Time",
      cell: ({ row }) => (
        <span>
          {row?.original?.dispatchDate?.toUiDateFormat()} <span className="text-light">{row?.original?.dispatchTime?.toUiTimeFormat()}</span>
        </span>
      ),
      size: 100,
    },
    // {
    //   accessorKey: "pros",
    //   header: "PROs",
    //   cell: ({ getValue }) =>
    //     <span
    //       onClick={() => setShowMainfestFreightDetailsModal(true)}
    //       style={{ color: '#2F80ED', cursor: 'pointer' }}
    //     >
    //       {getValue() as string}
    //     </span>,
    //   size: 50,
    // },
    // {
    //   accessorKey: "pieces",
    //   header: "Pieces",
    //   cell: ({ getValue }) =>
    //     <span
    //       onClick={() => setShowMainfestFreightDetailsModal(true)}
    //       style={{ color: '#2F80ED', cursor: 'pointer' }}
    //     >
    //       {getValue() as string}
    //     </span>,
    //   size: 50,
    // },
    // {
    //   accessorKey: "weight",
    //   header: "Weight",
    //   cell: ({ getValue }) =>
    //     <span
    //       onClick={() => setShowMainfestFreightDetailsModal(true)}
    //       style={{ color: '#2F80ED', cursor: 'pointer' }}
    //     >
    //       {getValue() && getValue() as string + " lbs"}
    //     </span>,
    //   size: 50,
    // },
    // {
    //   accessorKey: "eta",
    //   header: "ETA",
    //   cell: ({ row }) => {
    //     return (
    //       <span
    //         onClick={() => setShowMainfestFreightDetailsModal(true)}
    //         style={{ color: '#2F80ED', cursor: 'pointer' }}
    //       >
    //         {[
    //           row?.original?.eta?.toCustomDateFormat("MM/DD"),
    //           // row?.original?.dispatchDate.toCustomDateFormat("MM/DD")
    //         ]
    //           .filter(Boolean)
    //           .join(" - ")}
    //       </span>
    //     );
    //   },
    //   size: 50,
    // },
    {
      accessorKey: "driver",
      header: "Driver",
      size: 80,
      cell: ({ row }) => (
        <span>
          {row.original.driverName}{" "}
          <span className="text-light">{row.original.driverId}</span>
        </span>
      ),
      // cell: ({ row, column, getValue }) => {
      //   const value = getValue() as string;
      //   const isEditing =
      //     editingCell?.rowId === row.id &&
      //     editingCell?.columnId === column.id;

      //   if (isEditing) {
      //     return (
      //       <div style={{ display: "flex", gap: 4 }}>
      //         <LabeledSelectInput
      //           name="driver"
      //           label="Driver"
      //           labelMode={LabelModes.column}
      //           placeholder="Search Driver"
      //           required
      //           value={driverFormValue}
      //           onValueChange={onChangeDriver => setDriverFormValue(onChangeDriver || null)}
      //           inputValue={driverSearch}
      //           onInputChange={setDriverSearch}
      //           loadOptions={loadDriverOptions}
      //           cacheOptions
      //           async
      //           options={[]}
      //           className="xgs-dispatch-load-modal__field"
      //         />
      //         {/* <button onClick={() => saveEdit(row, column.id)}>✔</button>
      //         <button onClick={cancelEdit}>✖</button> */}
      //       </div>
      //     );
      //   }

      //   return (
      //     <div style={{ display: "flex", gap: 2, alignItems: "center" }}>
      //       <span>{value || "-"}</span>
      //       <EditIcon
      //         width={12}
      //         height={12}
      //         style={{ cursor: "pointer" }}
      //         onClick={(e) => {
      //           e.stopPropagation();
      //           startEdit(row, column.id, value);
      //         }}
      //       />
      //     </div>
      //   );
      // },
    },
    {
      accessorKey: "trailerNumber",
      header: "Trailer",
      size: 80,
      cell: (cellProps) => (
        <span>
          {cellProps?.getValue() && "TRL - " + cellProps?.getValue()}
        </span>
      ),
      // cell: ({ row, column, getValue }) => {
      //   const value = getValue() as string;
      //   const isEditing =
      //     editingCell?.rowId === row.id &&
      //     editingCell?.columnId === column.id;

      //   if (isEditing) {
      //     return (
      //       <LabeledSelectInput
      //         name="trailer"
      //         label="Trailer"
      //         labelMode={LabelModes.column}
      //         placeholder="Start Typing..."
      //         required
      //         value={trailerFormValue}
      //         onValueChange={onChangeTrailer => setTrailerFormValue(onChangeTrailer || null)}
      //         loadOptions={loadTrailerOptions}
      //         className="xgs-dispatch-load-modal__field"
      //         options={[]}
      //         async
      //       />
      //     );
      //   }

      //   return (
      //     <div style={{ display: "flex", gap: 1, alignItems: "center" }}>
      //       <span>{value ? `TRL - ${value}` : "-"}</span>
      //       <EditIcon
      //         width={12}
      //         height={12}
      //         style={{ cursor: "pointer", flexShrink: 0 }}
      //         onClick={(e) => {
      //           e.stopPropagation();
      //           startEdit(row, column.id, value);
      //         }}
      //       />
      //     </div>
      //   );
      // },
    },
    {
      accessorKey: "tractorNumber",
      header: "Tractor",
      size: 80,
      cell: (cellProps) => (
        <span>
          {cellProps?.getValue() && "TRC - " + cellProps?.getValue()}
        </span>
      ),
      // cell: ({ row, column, getValue }) => {
      //   const value = getValue() as string;
      //   const isEditing =
      //     editingCell?.rowId === row.id &&
      //     editingCell?.columnId === column.id;

      //   if (isEditing) {
      //     return (
      //       <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
      //         <LabeledSelectInput
      //           name="tractor"
      //           label="Tractor"
      //           labelMode={LabelModes.column}
      //           placeholder="Start Typing..."
      //           required
      //           value={tractorFormValue}
      //           options={[]}
      //           className="xgs-dispatch-load-modal__field"
      //           onValueChange={onChangeTractor => setTractorFormValue(onChangeTractor || null)}
      //           loadOptions={loadTrailerOptions}
      //           async
      //         />
      //       </div>
      //     );
      //   }

      //   return (
      //     <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
      //       <span>{value ? `TRC - ${value}` : "-"}</span>
      //       <EditIcon
      //         width={12}
      //         height={12}
      //         style={{ cursor: "pointer", flexShrink: 0 }}
      //         onClick={(e) => {
      //           e.stopPropagation();
      //           startEdit(row, column.id, value);
      //         }}
      //       />
      //     </div>
      //   );
      // },
    },
  ];

  const subColumns: ColumnDef<LoadboardListManifestTrip>[] = [
    {
      accessorKey: "tripNumber",
      header: "Trip #",
      size: 20
    },
    {
      accessorKey: "doorNumber",
      header: "Door",
      size: 80,
      cell: ({ getValue }) => {
        return (
          <>
            {getValue() && <span>D-{getValue()}</span>}
          </>
        );
      }
    },
    {
      accessorKey: "doorPriority",
      header: "!",
      size: 20,
    },
    {
      accessorKey: "originTerminalName",
      header: "Origin",
      cell: ({ row }) => (
        <span>
          {row?.original?.originTerminalAirportCode} - {row?.original?.originTerminalName} <span className="text-light">{row?.original?.originTerminalNumber}</span>
        </span>
      ),
      size: 100,
    },
    {
      accessorKey: "destinationTerminalName",
      header: "Destination",
      cell: ({ row }) => (
        <span>
          {row?.original?.destinationTerminalAirportCode} - {row?.original?.destinationTerminalName} <span className="text-light">{row?.original?.destinationTerminalNumber}</span>
        </span>
      ),
      size: 100,
    },
    {
      accessorKey: "loadedWeight",
      header: "Load %",
      size: 50,
      cell: ({ getValue }) => {
        const loadedWeight = Number(getValue()) || 0;
        const MAX_CAPACITY = 42000;

        const loadPercentage = Math.min(
          Math.round((loadedWeight / MAX_CAPACITY) * 100),
          100
        );

        return (
          <ProgressBar
            percentage={loadPercentage}
            additionalInfo={showPrettyTime(`${loadPercentage}%`)}
            multicolor
          />
        );
      },
    },
    {
      accessorKey: "carrierName",
      header: "Carrier",
      cell: ({ row }) => (
        <span>
          {row?.original?.carrierName} <span className="text-light">{row?.original?.carrierNumber}</span>
        </span>
      ),
      size: 70,
    },
    {
      id: "laneStatussab",
      accessorKey: "doorManifestStatus",
      header: "Lane Status",
      size: 50,
      cell: ({ getValue }) => {
        const colorMap = {
          DEPARTED: TagColor.GREEN,
          LOADING: TagColor.BLUE,
          CLOSED: TagColor.ORANGE,
          UNASSIGNED: TagColor.GREY,
        };

        const value = getValue();

        return (
          <Tag mods={{ color: colorMap[value] ?? TagColor.GREY }}>
            {value}
          </Tag>
        );
      }
    },
    {
      accessorKey: "plannedCloseTime",
      header: "Plan Close Time",
      cell: ({ row }) => (
        <span>
          {row?.original?.plannedCloseDate?.toUiDateFormat()} <span className="text-light">{row?.original?.plannedCloseTime?.toUiTimeFormat()}</span>
        </span>
      ),
      size: 70,
    },
    {
      accessorKey: "dispatchTime",
      header: "Dispatch Time",
      cell: ({ row }) => (
        <span>
          {row?.original?.dispatchDate?.toUiDateFormat()} <span className="text-light">{row?.original?.dispatchTime?.toUiTimeFormat()}</span>
        </span>
      ),
      size: 70,
    },
    // {
    //   accessorKey: "pros",
    //   header: "PROs",
    //   cell: ({ getValue }) =>
    //     <span
    //       onClick={() => setShowMainfestFreightDetailsModal(true)}
    //       style={{ color: '#2F80ED', cursor: 'pointer' }}
    //     >
    //       {getValue() as string}
    //     </span>,
    //   size: 50,
    // },
    // {
    //   accessorKey: "pieces",
    //   header: "Pieces",
    //   cell: ({ getValue }) =>
    //     <span
    //       onClick={() => setShowMainfestFreightDetailsModal(true)}
    //       style={{ color: '#2F80ED', cursor: 'pointer' }}
    //     >
    //       {getValue() as string}
    //     </span>,
    //   size: 50,
    // },
    // {
    //   accessorKey: "weight",
    //   header: "Weight",
    //   cell: ({ getValue }) =>
    //     <span
    //       onClick={() => setShowMainfestFreightDetailsModal(true)}
    //       style={{ color: '#2F80ED', cursor: 'pointer' }}
    //     >
    //       {getValue() && getValue() as string + " lbs"}
    //     </span>,
    //   size: 50,
    // },
    // {
    //   accessorKey: "eta",
    //   header: "ETA",
    //   cell: ({ row }) => {
    //     return (
    //       <span
    //         onClick={() => setShowMainfestFreightDetailsModal(true)}
    //         style={{ color: '#2F80ED', cursor: 'pointer' }}
    //       >
    //         {[
    //           row?.original?.eta?.toCustomDateFormat("MM/DD"),
    //           // row?.original?.dispatchDate.toCustomDateFormat("MM/DD")
    //         ]
    //           .filter(Boolean)
    //           .join(" - ")}
    //       </span>
    //     );
    //   },
    //   size: 50,
    // },
    {
      accessorKey: "driver",
      header: "Driver",
      cell: ({ row }) => (
        <span>
          {row.original.driverName}{" "}
          <span className="text-light">{row.original.driverId}</span>
        </span>
      ),
      size: 50,
    },
    {
      accessorKey: "trailerNumber",
      header: "Trailer",
      cell: (cellProps) => (
        <span>
          {cellProps?.getValue() && "TRL - " + cellProps?.getValue()}
        </span>
      ),
      size: 50,
    },
    {
      accessorKey: "tractorNumber",
      header: "Tractor",
      cell: (cellProps) => (
        <span>
          {cellProps?.getValue() && "TRC - " + cellProps?.getValue()}
        </span>
      ),
      size: 50,
    },
  ];

  const renderSubComponent = (row: any) => {
    const parentManifestNumber = row.original.manifestNumber;

    const subTrips = allTrips.filter(
      (item) => item.manifestNumber === parentManifestNumber
    );

    if (subTrips.length <= 1) return null;

    return (
      <div style={{ padding: "10px 40px", background: "#0000000f" }}>
        <Table
          data={subTrips}
          columns={subColumns}
          responsive
          rowHeight={30}
        />
      </div>
    );
  };

  return (
    <ContentContainer
      pageId={PAGE_IDS.LOADBOARD_DISPATCH}
      titleComponent={
        <ContentContainerToolbar
          title="Load Board & Dispatch"
          mods={{ "full-width": true }}
          tooltip={"This module is in Preview. Certain data and features may be incomplete or under development."}
          status={"PREVIEW"}
        >
        </ContentContainerToolbar>
      }
    >
      <div className="xgs-loadboard-dispatch">
        <div className="xgs-loadboard-dispatch xgs-list__controls">
          <Formik
            enableReinitialize={false}
            initialValues={{
              doorManifestStatus: filterStatus,
              carrierNumber: filterType,
              doorNumber: filterDoor,
              manifestDestinationTerminalNumber: filterDest
            }}
            onSubmit={onSearchStart}
          >
            {({ getFieldProps, resetForm }) => (
              <div className="xgs-loadboard-dispatch xgs-list__controls__search">
                <LabeledTextInput
                  name="manifestNumberSearch"
                  labelMode={LabelModes.column}
                  className="search-manifest"
                  label="Search Manifest"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.currentTarget.value)}
                  onKeyDown={(e) => e.key === "Enter" && onSearchStart()}
                  placeholder="e.g. 100234"
                />

                <LabeledDateRangeInput
                  label="Date Range:"
                  labelMode={LabelModes.column}
                  className="xgs-loadboard-dispatch__filter-select__date-range"
                  start={tempDateRange.start}
                  end={tempDateRange.end}
                  onStartChange={(val) => setTempDateRange(prev => ({ ...prev, start: val }))}
                  onEndChange={(val) => setTempDateRange(prev => ({ ...prev, end: val }))}
                />

                <XGSFormSelect
                  name="doorManifestStatus"
                  label="Lane Status"
                  className="xgs-loadboard-dispatch__filter-select__loadStatus"
                  placeholder="All Status"
                  options={statusOptions}
                  value={getOption(statusOptions, filterStatus)}
                  onValueChange={(opt) => setFilterStatus(opt?.value || "")}
                  labelMode={LabelModes.column}
                  formikProps={{ getFieldProps }}
                />

                <XGSFormSelect
                  name="carrierNumber"
                  label="Carrier"
                  className="xgs-loadboard-dispatch__filter-select"
                  placeholder="All Carrier"
                  isLoading={isCarrierLoading}
                  options={carrierOptions}
                  value={getOption(carrierOptions, filterType)}
                  onValueChange={(opt) =>
                    setFilterType(opt?.value ? Number(opt.value) : undefined)
                  }
                  labelMode={LabelModes.column}
                  formikProps={{ getFieldProps }}
                />

                <XGSFormSelect
                  name="doorNumber"
                  label="Door"
                  className="xgs-loadboard-dispatch__filter-select__door"
                  placeholder="All Door"
                  isLoading={isDoorsLoading}
                  options={doorOptions}
                  value={getOption(doorOptions, filterDoor)}
                  onValueChange={(opt) => setFilterDoor(opt?.value || "")}
                  labelMode={LabelModes.column}
                  formikProps={{ getFieldProps }}
                />

                <XGSFormSelect
                  name="manifestDestinationTerminalNumber"
                  label="Destination"
                  className="xgs-loadboard-dispatch__filter-select__destination"
                  placeholder="All Destination"
                  isLoading={destinationterminalsLoading}
                  options={destinationterminalOptions}
                  value={getOption(destinationterminalOptions, filterDest)}
                  onValueChange={(opt) => setFilterDest(opt?.value ? Number(opt.value) : undefined)}
                  labelMode={LabelModes.column}
                  formikProps={{ getFieldProps }}
                />
                <Button
                  className="xgs-list__controls__button"
                  theme={ButtonThemes.blue}
                  disabled={isManifestLoading}
                  onClick={onSearchStart}>
                  Search
                </Button>

                <Button
                  className="xgs-list__controls__button"
                  theme={ButtonThemes.gray}
                  onClick={onSearchClear(resetForm)}>
                  Clear Filters
                </Button>
              </div>
            )}
          </Formik>

          {/* <div className="xgs-list__controls__actions">
            <Switch
              onChange={onShowAvailableFreightChanged}
              checked={showConfirmedProbills}
              width={32}
              height={20}
              onColor="#2F80ED"
              checkedIcon={false}
              uncheckedIcon={false}
            />
            <span>Show Available Freight</span>
          </div> */}
        </div>

        {manifestError && (
          <XGSErrorMessage>Error loading Loadboard manifests. Please try again.</XGSErrorMessage>
        )}

        {!manifestError && (
          <>
            <div className="xgs-loadboard-dispatch__orders table-demo">
              <Table
                data={manifestData || []}
                columns={columns}
                isLoading={isManifestLoading}
                renderSubComponent={renderSubComponent}
                getRowCanExpand={(row) =>
                  allTrips.filter(
                    (item) => item.manifestNumber === row.original.manifestNumber
                  ).length > 1
                }
                responsive={true}
                rowHeight={50}
                enableRowSelection={false}
                noResultsText="There are no records available"
                sorting={true}
                minTableHeight={50}
              />
              
              <AssignDoorModal
                manifestNumber={editingManifestNumber || 0}
                destinationTerminal={4}
                // originTerminal={1}
                show={showAssignDoorModal}
                onClose={() => {
                  setShowAssignDoorModal(false);
                  setAssignDoorInitialValues(undefined);
                  setEditingManifestNumber(undefined);
                }}
                onSave={() => {
                  setShowAssignDoorModal(false);
                  setAssignDoorInitialValues(undefined);
                  setEditingManifestNumber(undefined);
                  refetch();
                }}
                initialValues={assignDoorInitialValues}
              />
              <CloseDoorModal
                show={showCloseDoorModal}
                closeddoordata={closedDoorData}
                onClose={() => {
                  setShowCloseDoorModal(false);
                  setClosedDoorData(null);
                }}
              />
              <DispatchLoadModal
                show={showDispatchModal}
                dispatchdata={dispatchData}
                onClose={() => {
                  setShowDispatchModal(false);
                  setDispatchData(null);
                }}
              />
              {/* <MainfestFreightDetails
                show={showMainfestFreightDetailsModal}
                onClose={() => setShowMainfestFreightDetailsModal(false)}
                onSave={(data) => {
                  console.log("Door assigned:", data);
                  setShowMainfestFreightDetailsModal(false);
                }}
              /> */}
            </div>
          </>
        )}
      </div>
    </ContentContainer>
  );
};

export default LoadboardDispatchList;